import { useCountdown } from "@redotech/react-util/countdown";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { CustomerSignInMessageType } from "@redotech/redo-model/conversation/message-parsers/customer-sign-in-message-parser";
import { isValidEmail } from "@redotech/util/email";
import { memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { Flex } from "../../flex";
import { OtpInput } from "../../otp-input";
import { Text } from "../../text";
import { HandleCustomerSignInFn } from "../common";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";

export interface CustomerSignInProps {
  message: CustomerSignInMessageType;
  conversationId: string;
  readOnly: boolean;
  onSubmitEmail: (email: string) => Promise<{ fourDigitCodeId: string }>;
  onVerifyOtp: (
    fourDigitCodeId: string,
    otp: string,
    email: string,
  ) => Promise<{ success: boolean; customerAuthToken?: string }>;
  onSignInComplete: HandleCustomerSignInFn;
}

export const CustomerSignIn = memo(function CustomerSignIn({
  message,
  conversationId,
  readOnly,
  onSubmitEmail,
  onVerifyOtp,
  onSignInComplete,
}: CustomerSignInProps) {
  const [currentStep, setCurrentStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [fourDigitCodeId, setFourDigitCodeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [countdown, startCountdown] = useCountdown();

  const reason = message.structuredContent?.reason;

  const emailIsValid = isValidEmail(email.trim());

  const [emailLoad, doEmailLoad] = useTriggerLoad(async () => {
    if (!emailIsValid) {
      setError("Please enter a valid email address");
      return;
    }

    setError(null);
    const result = await onSubmitEmail(email.trim());
    setFourDigitCodeId(result.fourDigitCodeId);
    setCurrentStep("otp");
    startCountdown(30);
  });

  const [otpLoad, doOtpLoad] = useTriggerLoad(async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      setError("Please enter the 4-digit verification code");
      return;
    }

    setError(null);
    const result = await onVerifyOtp(fourDigitCodeId, otpValue, email.trim());

    if (result.success && result.customerAuthToken) {
      await onSignInComplete({
        conversationId,
        messageId: message._id,
        customerEmail: email.trim(),
        customerAuthToken: result.customerAuthToken,
      });
      setSignedInEmail(email.trim());
    } else {
      setError("Invalid verification code. Please try again.");
    }
  });

  const handleResendCode = useHandler(async () => {
    if (countdown > 0) return;
    setOtp(["", "", "", ""]);
    setError(null);
    try {
      const result = await onSubmitEmail(email.trim());
      setFourDigitCodeId(result.fourDigitCodeId);
      startCountdown(30);
    } catch {
      setError("Failed to send verification code. Please try again.");
    }
  });

  const handleChangeEmail = useHandler(() => {
    setCurrentStep("email");
    setOtp(["", "", "", ""]);
    setError(null);
  });

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !emailLoad.pending) {
      doEmailLoad();
    }
  };

  const isLoading = emailLoad.pending || otpLoad.pending;

  // Compute the error to display - prioritize validation errors, then async errors
  const displayError =
    error ||
    (emailLoad.error
      ? "Failed to send verification code. Please try again."
      : null) ||
    (otpLoad.error ? "Failed to verify code. Please try again." : null);

  // Build the title based on reason - e.g., "to claim your discount" becomes "Sign in to claim your discount!"
  const title = reason ? `Sign in ${reason}!` : "Sign in to continue";

  const pendingContent = (
    <Flex dir="column" gap="lg" px="lg" py="md" w="full">
      <Flex align="center" dir="column" gap="sm">
        <Text
          fontSize="md"
          fontWeight="semibold"
          textAlign="center"
          textColor="primary"
        >
          {title}
        </Text>
      </Flex>

      {currentStep === "email" ? (
        <>
          <RedoTextInput
            inputTypeHint="email"
            label="Email address"
            onKeyDown={handleEmailKeyDown}
            placeholder="Enter your email"
            setValue={setEmail}
            size="md"
            state={isLoading || readOnly ? "readonly" : "default"}
            value={email}
          />
          <RedoButton
            disabled={isLoading || readOnly || !emailIsValid}
            hierarchy="primary"
            onClick={() => doEmailLoad()}
            pending={isLoading}
            size="md"
            text="Send verification code"
          />
        </>
      ) : (
        <>
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" textColor="secondary">
              Enter the 4-digit code sent to {email}
            </Text>
            <OtpInput numeric onChange={setOtp} values={otp} />
          </Flex>

          <RedoButton
            disabled={isLoading || readOnly || otp.join("").length !== 4}
            hierarchy="primary"
            onClick={() => doOtpLoad()}
            pending={isLoading}
            size="md"
            text="Verify and sign in"
          />

          <Flex align="center" dir="column" gap="md">
            <RedoButton
              disabled={isLoading || countdown > 0}
              hierarchy="tertiary"
              onClick={handleResendCode}
              size="sm"
              text={
                countdown > 0
                  ? `Resend code (${countdown}s)`
                  : "Send another code"
              }
            />
            <RedoButton
              disabled={isLoading}
              hierarchy="tertiary"
              onClick={handleChangeEmail}
              size="sm"
              text="Change email"
            />
          </Flex>
        </>
      )}

      {displayError && (
        <Text fontSize="sm" textAlign="center" textColor="error">
          {displayError}
        </Text>
      )}
    </Flex>
  );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: pendingContent,
        confirmed: (
          <GenericConfirmedState
            subtitle={signedInEmail ? `Signed in as ${signedInEmail}` : ""}
            title="Signed in successfully"
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="Please try again or contact support"
            title="Unable to sign in"
          />
        ),
      }}
      message={message}
    />
  );
});
